import { useCallback } from 'react';
import {
  AppState,
  ClientCoordinates,
  SelectionCoordinates,
  SelectionMode,
  SelectionModes,
  ViewBox,
} from '../../../types';
import { resolveSelectionId } from '../../../helpers/groupHelpers';
import { findSelectedElements } from '../helpers';

export interface CanvasInteractionDeps {
  appState: AppState;
  setSelectedElements: React.Dispatch<React.SetStateAction<string[]>>;
  selectionCoordinates: SelectionCoordinates;
  setSelectionCoordinates: React.Dispatch<
    React.SetStateAction<SelectionCoordinates>
  >;
  selectionMode: SelectionMode;
  setSelectionMode: React.Dispatch<React.SetStateAction<SelectionMode>>;
  viewBox: ViewBox;
  setViewBox: React.Dispatch<React.SetStateAction<ViewBox>>;
  isMouseDown: boolean;
  isSpacePressed: boolean;
}

export const useCanvasInteraction = (hooks: CanvasInteractionDeps) => {
  const {
    appState,
    setSelectedElements,
    selectionCoordinates,
    setSelectionCoordinates,
    selectionMode,
    setSelectionMode,
    viewBox,
    setViewBox,
    isMouseDown,
    isSpacePressed,
  } = hooks;

  const onStartPanning = useCallback(
    (movementX: number, movementY: number) => {
      setSelectedElements([]);
      setSelectionMode({
        ...selectionMode,
        type: SelectionModes.QuickPanning,
      });
      setViewBox((vB: ViewBox) => ({
        ...vB,
        startPoint: { x: movementX, y: movementY },
      }));
    },
    [setSelectedElements, setSelectionMode, selectionMode, setViewBox]
  );

  const onPanning = useCallback(
    (movementX: number, movementY: number) => {
      if (!(isMouseDown || (isSpacePressed && isMouseDown))) {
        return;
      }
      const { startPoint, scale } = viewBox;
      const endPoint = { x: movementX, y: movementY };
      const dx = (startPoint.x - endPoint.x) * scale;
      const dy = (startPoint.y - endPoint.y) * scale;
      const movedViewBox = {
        x: viewBox.x + dx,
        y: viewBox.y + dy,
        w: viewBox.w,
        h: viewBox.h,
      };
      setViewBox((vB: ViewBox) => ({ ...vB, ...movedViewBox }));
    },
    [isMouseDown, isSpacePressed, viewBox, setViewBox]
  );

  const onStartMultiSelecting = useCallback(
    (clientCoordinates: ClientCoordinates) => {
      setSelectedElements([]);
      setSelectionMode({
        ...selectionMode,
        elementType: undefined,
        lineType: undefined,
        type: SelectionModes.MultiSelecting,
      });
      const initialX = clientCoordinates.x;
      const initialY = clientCoordinates.y;
      setSelectionCoordinates({
        ...selectionCoordinates,
        initialX,
        initialY,
        startX: initialX,
        startY: initialY,
      });
    },
    [
      setSelectedElements,
      setSelectionMode,
      selectionMode,
      setSelectionCoordinates,
      selectionCoordinates,
    ]
  );

  const onMultiSelecting = useCallback(
    (clientCoordinates: ClientCoordinates) => {
      const currentX = clientCoordinates.x;
      const currentY = clientCoordinates.y;
      const { startX, startY } = selectionCoordinates;
      if (startX === null || startY === null) return;

      const activeGroupId =
        selectionMode.type === SelectionModes.GroupEditing
          ? selectionMode.groupId
          : undefined;

      const rawHits = findSelectedElements(
        appState.elements,
        startX,
        startY,
        currentX,
        currentY
      ).filter((id) => id !== activeGroupId);

      const resolvedIds = [
        ...new Set(
          rawHits.map((id) =>
            resolveSelectionId(id, appState.elements, activeGroupId)
          )
        ),
      ];

      setSelectedElements(resolvedIds);
      setSelectionCoordinates({
        ...selectionCoordinates,
        currentX,
        currentY,
      });
    },
    [
      selectionCoordinates,
      selectionMode,
      appState.elements,
      setSelectedElements,
      setSelectionCoordinates,
    ]
  );

  const resetSelection = useCallback(() => {
    setSelectedElements([]);
    setSelectionMode({
      ...selectionMode,
      type: SelectionModes.None,
      elementType: undefined,
      lineType: undefined,
    });
  }, [setSelectedElements, setSelectionMode, selectionMode]);

  const resetMultiSelection = useCallback(() => {
    setSelectionCoordinates({
      ...selectionCoordinates,
      startX: null,
      startY: null,
      currentX: null,
      currentY: null,
    });
  }, [setSelectionCoordinates, selectionCoordinates]);

  return {
    onStartPanning,
    onPanning,
    onStartMultiSelecting,
    onMultiSelecting,
    resetSelection,
    resetMultiSelection,
  };
};
